// ========================================
// COMPONENTE DRIVEVIEW (VISTA PRINCIPAL DEL DRIVE)
// Maneja la navegación entre carpetas, subcarpetas y archivos
// con índice de navegación (breadcrumb) tipo Google Drive.
// ========================================

import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import FoldersView from './FoldersView';           // Vista de carpetas
import FilesView from './FilesView';               // Vista de archivos dentro de una carpeta
import CreateFolderModal from './CreateFolderModal'; // Modal para crear nuevas carpetas
import FolderSettingsModal from './FolderSettingsModal'; // Modal para gestionar permisos de una carpeta
import config from '../Componentes/config';
import { useCookies } from 'react-cookie';
import { registrarLog } from './logService';

const DriveView = () => {
    // ========================================
    // ESTADOS DEL COMPONENTE
    // ========================================
    const [showCreateModal, setShowCreateModal] = useState(false);   // Modal de crear carpeta
    const [settingsFolder, setSettingsFolder] = useState(null);      // Carpeta cuyo panel de ajustes está abierto
    const [folders, setFolders] = useState([]);                      // Subcarpetas del nivel actual
    const [loading, setLoading] = useState(false);                   // Estado de carga
    // path = ruta de navegación (breadcrumb). [] = raíz. Último elemento = carpeta actual.
    const [path, setPath] = useState([]);
    const [cookies] = useCookies(['selectedCard']);
    const idEmpresa = cookies.idEmpresa;
    const empresaSeleccionada = cookies.NombreEmpresa;
    const USUARIO_ID = cookies.usuario;

    // Carpeta actualmente abierta (null si estamos en la raíz)
    const currentFolder = path.length > 0 ? path[path.length - 1] : null;
    const parentId = currentFolder ? currentFolder.idCarpeta : '';

    // ========================================
    // CARGA DE DATOS
    // ========================================
    const loadFolders = useCallback(async () => {
        setLoading(true);
        try {
            const url = `${config.API_URL}/carpetas/${idEmpresa}?usuario=${encodeURIComponent(USUARIO_ID)}&padre=${encodeURIComponent(parentId)}`;
            const response = await fetch(url);
            if (response.ok) {
                const foldersData = await response.json();

                // Para cada carpeta, obtener el número de archivos
                const foldersWithFileCount = await Promise.all(
                    foldersData.map(async (folder) => {
                        try {
                            const filesResponse = await fetch(`${config.API_URL}/archivos/${folder.idCarpeta}`);
                            if (filesResponse.ok) {
                                const files = await filesResponse.json();
                                return { ...folder, fileCount: files.length };
                            }
                            return { ...folder, fileCount: 0 };
                        } catch {
                            return { ...folder, fileCount: 0 };
                        }
                    })
                );

                setFolders(foldersWithFileCount);
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error al cargar las carpetas',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#d33'
                });
            }
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Error de conexión',
                text: 'No se pudo conectar con el servidor',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#d33'
            });
        } finally {
            setLoading(false);
        }
    }, [idEmpresa, USUARIO_ID, parentId]);

    // Recargar subcarpetas cada vez que cambia el nivel de navegación
    useEffect(() => {
        loadFolders();
    }, [loadFolders]);

    // ========================================
    // NAVEGACIÓN
    // ========================================

    // Entrar en una carpeta (agregar al breadcrumb)
    const handleFolderSelect = (folder) => {
        setPath(prev => [...prev, folder]);
    };

    // Navegar a un punto del breadcrumb (index = -1 => raíz)
    const navigateTo = (index) => {
        if (index < 0) {
            setPath([]);
        } else {
            setPath(prev => prev.slice(0, index + 1));
        }
    };

    // ========================================
    // GESTIÓN DE CARPETAS
    // ========================================
    const handleCreateFolder = async (folderName) => {
        try {
            const response = await fetch(`${config.API_URL}/carpetas`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nombreCarpeta: folderName,
                    usuarioCrea: USUARIO_ID,
                    Empresa: idEmpresa,
                    idCarpetaPadre: parentId || null   // null = carpeta raíz; valor = subcarpeta
                })
            });

            if (response.ok) {
                await registrarLog({
                    Usuario: USUARIO_ID,
                    Modulo: 'CarpetaAdministrativa',
                    Detalle: `Carpeta creada: ${folderName}${currentFolder ? ` dentro de ${currentFolder.nombreCarpeta}` : ''} en empresa ${empresaSeleccionada}`,
                    Estado: 'Éxito',
                });

                await Swal.fire({
                    icon: 'success',
                    title: '¡Carpeta creada!',
                    text: 'La carpeta se creó exitosamente',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });

                setShowCreateModal(false);
                loadFolders();
            } else {
                const errorData = await response.json();
                await registrarLog({
                    Usuario: USUARIO_ID,
                    Modulo: 'CarpetaAdministrativa',
                    Detalle: `Error al crear carpeta ${folderName} - ${errorData.error || 'Error desconocido'}`,
                    Estado: 'Error',
                });
                await Swal.fire({
                    icon: 'error',
                    title: 'Error al crear carpeta',
                    text: errorData.error || 'Error al crear la carpeta',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#d33'
                });
            }
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Error de conexión',
                text: 'No se pudo conectar con el servidor',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#d33'
            });
        }
    };

    const handleDeleteFolder = async (folderId) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: '¿Eliminar carpeta?',
            text: '¿Estás seguro de que quieres eliminar esta carpeta? Esta acción no se puede deshacer.',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            reverseButtons: true
        });
        if (!result.isConfirmed) return;

        try {
            const response = await fetch(`${config.API_URL}/carpetas/${folderId}`, { method: 'DELETE' });
            if (response.ok) {
                const carpetaEliminada = folders.find(f => f.idCarpeta === folderId);
                const nombreCarpeta = carpetaEliminada ? carpetaEliminada.nombreCarpeta : `ID: ${folderId}`;
                await registrarLog({
                    Usuario: USUARIO_ID,
                    Modulo: 'CarpetaAdministrativa',
                    Detalle: `Carpeta eliminada: ${nombreCarpeta} de empresa ${empresaSeleccionada}`,
                    Estado: 'Éxito',
                });
                await Swal.fire({
                    icon: 'success',
                    title: '¡Carpeta eliminada!',
                    text: 'La carpeta se eliminó exitosamente',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
                loadFolders();
            } else {
                const errorData = await response.json();
                await registrarLog({
                    Usuario: USUARIO_ID,
                    Modulo: 'CarpetaAdministrativa',
                    Detalle: `Error al eliminar carpeta ID: ${folderId} - ${errorData.error || 'Error desconocido'}`,
                    Estado: 'Error',
                });
                await Swal.fire({
                    icon: 'error',
                    title: 'Error al eliminar',
                    text: errorData.error || 'Error al eliminar la carpeta',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#d33'
                });
            }
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Error de conexión',
                text: 'No se pudo conectar con el servidor',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#d33'
            });
        }
    };

    const handleRenameFolder = async (folder) => {
        const { value: nuevoNombre } = await Swal.fire({
            title: 'Renombrar carpeta',
            input: 'text',
            inputLabel: 'Nuevo nombre de la carpeta',
            inputValue: folder.nombreCarpeta,
            showCancelButton: true,
            confirmButtonText: 'Renombrar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#6c757d',
            inputValidator: (value) => {
                if (!value) return 'Debes ingresar un nombre para la carpeta';
                if (value.length > 50) return 'El nombre es demasiado largo';
            }
        });
        if (!nuevoNombre) return;

        try {
            const response = await fetch(`${config.API_URL}/carpetas/${folder.idCarpeta}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nuevoNombre: nuevoNombre.trim(), empresa: idEmpresa })
            });
            if (response.ok) {
                await Swal.fire({
                    icon: 'success',
                    title: '¡Carpeta renombrada!',
                    text: 'El nombre de la carpeta se cambió exitosamente',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
                loadFolders();
            } else {
                const errorData = await response.json();
                await Swal.fire({
                    icon: 'error',
                    title: 'Error al renombrar',
                    text: errorData.error || 'Error al renombrar la carpeta',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#d33'
                });
            }
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Error de conexión',
                text: 'No se pudo conectar con el servidor',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#d33'
            });
        }
    };

    // ¿El usuario puede crear contenido en el nivel actual?
    // En la raíz cualquiera puede crear su propia carpeta; dentro de una carpeta
    // solo quien la gestiona (creador o admin).
    const puedeCrearAqui = !currentFolder || currentFolder.puedeGestionar;

    // ========================================
    // RENDERIZADO
    // ========================================
    return (
        <div className="drive-view">
            {/* Índice de navegación (breadcrumb) */}
            <nav className="drive-breadcrumb">
                <span
                    className={`breadcrumb-item ${path.length === 0 ? 'active' : ''}`}
                    onClick={() => navigateTo(-1)}
                >
                    🏠 Inicio
                </span>
                {path.map((folder, index) => (
                    <span key={folder.idCarpeta} className="breadcrumb-segment">
                        <span className="breadcrumb-separator"> / </span>
                        <span
                            className={`breadcrumb-item ${index === path.length - 1 ? 'active' : ''}`}
                            onClick={() => navigateTo(index)}
                        >
                            {folder.nombreCarpeta}
                        </span>
                    </span>
                ))}
            </nav>

            {/* Encabezado */}
            <div className="drive-header">
                <div>
                    <h1 className="drive-title">
                        {currentFolder ? currentFolder.nombreCarpeta : 'Nube administrativa de ' + empresaSeleccionada}
                    </h1>
                </div>

                <div className="action-buttons">
                    {puedeCrearAqui && (
                        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                            + Nueva Carpeta
                        </button>
                    )}
                </div>
            </div>

            {/* Subcarpetas del nivel actual */}
            <FoldersView
                folders={folders}
                loading={loading}
                onFolderSelect={handleFolderSelect}
                onDeleteFolder={handleDeleteFolder}
                onRenameFolder={handleRenameFolder}
                onOpenSettings={(folder) => setSettingsFolder(folder)}
                hideEmptyState={!!currentFolder}
            />

            {/* Archivos: solo dentro de una carpeta */}
            {currentFolder && (
                <FilesView
                    folder={currentFolder}
                    onError={(error) => {
                        Swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: error,
                            confirmButtonText: 'Entendido',
                            confirmButtonColor: '#d33'
                        });
                    }}
                    onSuccess={(message) => {
                        Swal.fire({
                            icon: 'success',
                            title: '¡Éxito!',
                            text: message,
                            showConfirmButton: false,
                            timer: 2000,
                            timerProgressBar: true
                        });
                    }}
                />
            )}

            {showCreateModal && (
                <CreateFolderModal
                    parentName={currentFolder ? currentFolder.nombreCarpeta : null}
                    onClose={() => setShowCreateModal(false)}
                    onConfirm={handleCreateFolder}
                />
            )}

            {settingsFolder && (
                <FolderSettingsModal
                    folder={settingsFolder}
                    usuarioActual={USUARIO_ID}
                    onClose={() => setSettingsFolder(null)}
                />
            )}
        </div>
    );
};

export default DriveView;
